import { useEffect, useState } from 'react'
import { X, Loader2, Plus, Minus, Trash2 } from 'lucide-react'
import { postJson, patchJson, apiDelete } from '../../lib/api.js'
import { fmtMonthTH } from '../../lib/timetable.js'
import { SALARY_TYPES, salaryTypeIsDeduction, signedAmount, rowAmount, fmtSigned } from '../../lib/salary.js'
import EmployeePicker from './EmployeePicker.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'
import { Banner } from '../../kit'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 disabled:bg-slate-50 disabled:text-slate-400'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

/**
 * Add or edit ONE manual salary_em line (generate:false → survives gen2).
 *
 *   mode="add"   — pick employee + type + signed amount for {year, month}. Right
 *                  side drawer.
 *   mode="edit"  — `row` given; employee/period are fixed context, only type,
 *                  amount and remark are editable. The drawer also carries the
 *                  DELETE action (confirmed in-drawer).
 *
 * The amount is entered as a positive magnitude plus an earning/deduction toggle
 * (the row stores the signed number). The toggle defaults from the type but the
 * user can override it — a "หักสาย" can still be a positive correction.
 */
export default function ManualEntryModal({ mode = 'add', row, year, month, employeeName, onClose, onSaved }) {
  const isEdit = mode === 'edit'
  const initAmt = isEdit ? rowAmount(row) : 0

  const [employeeId, setEmployeeId] = useState(isEdit ? String(row?.employee || '') : '')
  const [type, setType] = useState(isEdit ? (row?.type || 'อื่นๆ') : 'อื่นๆ')
  const [isDeduction, setIsDeduction] = useState(isEdit ? initAmt < 0 : salaryTypeIsDeduction('อื่นๆ'))
  const [magnitude, setMagnitude] = useState(isEdit ? String(Math.abs(initAmt) || '') : '')
  const [remark, setRemark] = useState(isEdit ? (row?.remark || '') : '')
  const [saving, setSaving] = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Picking a type retargets the sign default (add-mode only — don't fight an
  // explicit edit-mode sign the user is looking at).
  function pickType(t) {
    setType(t)
    if (!isEdit) setIsDeduction(salaryTypeIsDeduction(t))
  }

  async function save() {
    const mag = Number(magnitude)
    if (!isEdit && !employeeId) { setErr('กรุณาเลือกพนักงาน'); return }
    if (!type) { setErr('กรุณาเลือกประเภทรายการ'); return }
    if (!Number.isFinite(mag) || mag <= 0) { setErr('กรุณาระบุจำนวนเงินที่มากกว่า 0'); return }
    const amount = signedAmount(mag, isDeduction)
    setSaving(true); setErr('')
    try {
      if (isEdit) {
        // `month` is sent but NOT editable — api 368's dblink input validates it
        // as a full record (salary_em.month is min:1/max:12), so omitting it 400s
        // before the stack runs. The proxy puts it on the query string so Xano
        // validates it without db.patch ever writing it. Prefer the ROW's own
        // month over the page filter; they match today, and this stays right if
        // the ledger ever shows more than one month at a time.
        const rowMonth = Number(row?.month) || month
        await patchJson(`/api/hr/salary/${row.id}`, { type, amount, remark: remark.trim(), month: rowMonth })
      } else {
        await postJson('/api/hr/salary', {
          year, month, employee: Number(employeeId), type, amount, remark: remark.trim(),
        })
      }
      onSaved?.()
    } catch (e) { setErr(String(e.message || e)) }
    finally { setSaving(false) }
  }

  async function doDelete() {
    setConfirmDel(false); setSaving(true); setErr('')
    try { await apiDelete(`/api/hr/salary/${row.id}`); onSaved?.() } // onSaved closes + reloads
    catch (e) { setErr(String(e.message || e)); setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40" onMouseDown={onClose} />
      <div className="relative w-full max-w-[460px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h2 className="text-[15.5px] font-semibold text-slate-900">
              {isEdit ? 'แก้ไขรายการ' : 'เพิ่มรายการเงินเดือน'}
            </h2>
            <p className="text-[12.5px] text-slate-500">
              {isEdit ? `${employeeName || `#${row?.employee}`} · ` : ''}{fmtMonthTH(year, month)}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* A row carrying recurring_uuid IS one installment of a หักประจำ plan: gen2 and
              the หักประจำ tab count it as one paid installment whatever its amount. So an
              edited amount silently desynchronises money from count (gen2 stops at N rows
              — a halved row leaves the rest uncollected). Say so; deleting is the clean
              way to give the installment back. */}
          {isEdit && row?.recurring_uuid && (
            <Banner tone="warn" title="รายการนี้เป็นงวดของหักประจำ">
              นับเป็นชำระแล้ว 1 งวด ไม่ว่ายอดจะเป็นเท่าใด — ถ้าต้องการคืนงวดนี้ให้ลบรายการ
              ไม่ควรแก้ยอดหรือประเภท
            </Banner>
          )}
          {!isEdit && (
            <div>
              <label className={LABEL}>พนักงาน</label>
              <EmployeePicker value={employeeId} onChange={setEmployeeId} className="w-full" />
            </div>
          )}

          <div>
            <label className={LABEL}>ประเภทรายการ</label>
            <select value={type} onChange={(e) => pickType(e.target.value)} className={FIELD}>
              {SALARY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className={LABEL}>จำนวนเงิน</label>
            <div className="flex items-stretch gap-2">
              <div className="inline-flex rounded-xl ring-1 ring-slate-200 overflow-hidden shrink-0">
                <button type="button" onClick={() => setIsDeduction(false)}
                  className={`inline-flex items-center gap-1 px-3 text-[13px] font-medium ${!isDeduction ? 'bg-emerald-50 text-emerald-700' : 'text-slate-500 hover:bg-slate-50'}`}>
                  <Plus size={14} /> รายรับ
                </button>
                <button type="button" onClick={() => setIsDeduction(true)}
                  className={`inline-flex items-center gap-1 px-3 text-[13px] font-medium border-l border-slate-200 ${isDeduction ? 'bg-rose-50 text-rose-700' : 'text-slate-500 hover:bg-slate-50'}`}>
                  <Minus size={14} /> รายหัก
                </button>
              </div>
              <input type="number" inputMode="decimal" min="0" step="0.01" value={magnitude}
                onChange={(e) => setMagnitude(e.target.value)} placeholder="0.00"
                className={`${FIELD} text-right tabular-nums`} />
            </div>
            <p className="text-[11.5px] text-slate-400 mt-1">
              รายหักจะถูกบันทึกเป็นจำนวนติดลบและนำไปหักออกจากยอดสุทธิ
            </p>
          </div>

          <div>
            <label className={LABEL}>หมายเหตุ</label>
            <textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)}
              placeholder="รายละเอียดเพิ่มเติม (ถ้ามี)" className={FIELD} />
          </div>

          {err && <div className="text-[12.5px] text-rose-500">{err}</div>}
        </div>

        <div className="border-t border-slate-100 px-5 py-3.5 flex items-center justify-between gap-2">
          {isEdit ? (
            <button onClick={() => setConfirmDel(true)} disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13.5px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
              <Trash2 size={15} /> ลบรายการ
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
            <button onClick={save} disabled={saving}
              className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              {saving && <Loader2 size={14} className="animate-spin" />} บันทึก
            </button>
          </div>
        </div>
      </div>

      {confirmDel && (
        <ConfirmDialog
          title="ลบรายการเงินเดือน"
          message={`ลบ "${type}" ${fmtSigned(signedAmount(Number(magnitude) || 0, isDeduction))}${row?.generate ? ' (รายการที่คำนวณไว้ — จะถูกสร้างใหม่เมื่อคำนวณเงินเดือนรอบนี้อีกครั้ง)' : ''}`}
          confirmLabel="ลบรายการ"
          tone="danger"
          onConfirm={doDelete}
          onCancel={() => setConfirmDel(false)}
        />
      )}
    </div>
  )
}
